// Browser-side pdfmake cover-page renderer.
//
// The TipTap-JSON -> pdfmake conversion (page geometry, styles, node walker) lives
// in @defectwise/pdf-report-core, shared with defects-management-reports-api's
// server-side PDF generation. This file is the thin browser-specific wrapper:
// it supplies the browser image resolver and drives pdfmake's client build.
//
// Scope: a SINGLE cover page (TipTap JSON -> pdfmake -> PDF). The defect-list render
// path is intentionally NOT shared (server-only, SQL-driven).
import type { Content as TipTapContent } from "@tiptap/react";
import type { TDocumentDefinitions } from "pdfmake/interfaces";
import {
  createDocDefinition,
  getTemplateDefaultStyles,
  getTemplateStyles,
  PdfCustomSectionBuilder,
  type ImageResolver,
  type TokenResolver,
  defaultPreviewTokenResolver,
} from "@defectwise/pdf-report-core";

import type { TImageManifestEntry } from "models";
import { resolveManifestImageSrcs } from "utils/templates/image-manifest";

export type {
  ImageResolver,
  TokenResolver,
} from "./resolvers";
export {
  defaultBrowserImageResolver,
  defaultPreviewTokenResolver,
} from "./resolvers";

export interface BuildCoverOptions {
  /** Resolves an image src (+ type) to a base64 data URL. */
  resolveImage: ImageResolver;
  /** Resolves field-token mentions to text. Defaults to the mention label. */
  resolveToken?: TokenResolver;
  /** Manifest to resolve generic `imageBlock.attrs.imageId` -> `attrs.src` from — required for any
   *  content that carries images, since @defectwise/pdf-report-core reads `src` directly and knows
   *  nothing about the manifest. Omit only for manifest-free content (tests, image-less probes). */
  imageManifest?: TImageManifestEntry[];
  /** Paired with `imageManifest` to build each resolved `src`'s auth query string. */
  storageToken?: string;
}

/** Coerce a TipTap `Content` value into the node array the converter expects. */
function toNodeArray(content: TipTapContent): any[] {
  if (Array.isArray(content)) return content;
  if (content && typeof content === "object") {
    // JSONContent doc node -> use its children
    return (content as any).content ?? [content];
  }
  return [];
}

/**
 * Build the pdfmake document definition for a single cover page (images resolved).
 *
 * Kept separate from {@link renderCoverPdf} so the same definition can later be run
 * through pdfmake's `pageBreakBefore` to extract page-break positions for the editor.
 */
export async function buildCoverDocDefinition(
  content: TipTapContent,
  opts: BuildCoverOptions,
): Promise<TDocumentDefinitions> {
  const doc = createDocDefinition();

  const builder = new PdfCustomSectionBuilder({
    resolveImage: opts.resolveImage,
    resolveToken: opts.resolveToken ?? defaultPreviewTokenResolver,
    addToToc: false,
  });

  const resolvedContent = opts.imageManifest
    ? resolveManifestImageSrcs(content, opts.imageManifest, opts.storageToken ?? "")
    : content;

  const body = await builder.generate({ content: toNodeArray(resolvedContent as TipTapContent) }, false);

  doc.content = body;
  doc.styles = getTemplateStyles();
  doc.defaultStyle = getTemplateDefaultStyles();

  return doc;
}

/**
 * Render a single cover page to a PDF `Blob` for preview.
 *
 * pdfmake + its font vfs are imported lazily so that merely building a doc
 * definition (e.g. for pagination work or tests) does not pull in the heavy bundle.
 *
 * Used by the dev-only "Preview Cover (PDF)" sidebar button
 * (pages/template-builder/components/cover-pdf-preview) as well as
 * page-breaks.ts's headless pagination pass.
 */
export async function renderCoverPdf(
  content: TipTapContent,
  opts: BuildCoverOptions,
): Promise<Blob> {
  const doc = await buildCoverDocDefinition(content, opts);

  const pdfMakeModule: any = await import("pdfmake/build/pdfmake");
  const vfsModule: any = await import("pdfmake/build/vfs_fonts");

  const createPdf = pdfMakeModule.createPdf ?? pdfMakeModule.default?.createPdf;
  // pdfmake 0.2.x vfs_fonts exports the vfs map directly (keys are the .ttf names);
  // tolerate default/`vfs` wrappers across versions. Matches the server's
  // createPdf(doc, undefined, undefined, vfs_fonts) call for font parity.
  const vfs = vfsModule.vfs ?? vfsModule.default ?? vfsModule;

  const pdfDoc = createPdf(doc, undefined, undefined, vfs);

  return await new Promise<Blob>((resolve, reject) => {
    try {
      pdfDoc.getBlob((blob: Blob) => resolve(blob));
    } catch (ex) {
      reject(ex);
    }
  });
}
